import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import type { Order, CustomsDocument } from "@/lib/types";
import {
  getStoredQuotes,
  formatWhatsAppFollowUpUrl,
  formatEmailFollowUpUrl,
  type UserQuoteRecord,
} from "@/lib/user-quotes";
import {
  Loader2,
  Package,
  FileText,
  ClipboardList,
  TrendingUp,
  ChevronRight,
  Clock,
  ShieldCheck,
  AlertCircle,
  MessageCircle,
  Mail,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getLocalizedMeta } from "@/lib/utils/seo";

export const Route = createFileRoute("/account/")({
  head: () => ({ meta: getLocalizedMeta("account") }),
  component: Dashboard,
});

function Dashboard() {
  const { t } = useI18n();
  const { user } = useAuth();

  const [orders, setOrders] = useState<Order[]>([]);
  const [documents, setDocuments] = useState<CustomsDocument[]>([]);
  const [quotes, setQuotes] = useState<UserQuoteRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    async function loadDashboardData() {
      // 1. Local quotes for buyer
      const localQuotes = getStoredQuotes(user?.email);

      try {
        const [ordersRes, docsRes, quotesRes] = await Promise.all([
          fetch("/api/portal/orders"),
          fetch("/api/portal/customs"),
          fetch("/api/portal/quotes").catch(() => null),
        ]);

        if (active) {
          if (ordersRes && ordersRes.ok) {
            const ordersData = await ordersRes.json();
            setOrders(ordersData.data || []);
          }
          if (docsRes && docsRes.ok) {
            const docsData = await docsRes.json();
            setDocuments(docsData.data || []);
          }

          const allQuotes = [...localQuotes];
          if (quotesRes && quotesRes.ok) {
            const serverQuotesData = await quotesRes.json();
            const serverQuotes = Array.isArray(serverQuotesData.data)
              ? (serverQuotesData.data as UserQuoteRecord[])
              : [];
            for (const sq of serverQuotes) {
              if (!allQuotes.some((m) => m.id.toLowerCase() === sq.id.toLowerCase())) {
                allQuotes.push(sq);
              }
            }
          }
          setQuotes(allQuotes);
        }
      } catch (err) {
        console.error("Error loading dashboard data:", err);
        if (active) setQuotes(localQuotes);
      } finally {
        if (active) setLoading(false);
      }
    }
    loadDashboardData();
    return () => {
      active = false;
    };
  }, [user?.email]);

  const activeQuotesCount = quotes.filter((q) => q.status !== "Closed").length;
  const completedOrdersCount = orders.filter((o) => o.status === "Shipped").length;
  const pendingDocsCount = documents.length;

  if (loading) {
    return (
      <div className="flex h-[40vh] items-center justify-center">
        <div className="text-center space-y-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="text-sm font-semibold text-navy/80">{t("account.loadingDashboard")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      <div>
        <h1 className="font-display text-4xl font-black tracking-tight text-navy">
          {t("account.dashboard.welcome")}, {user?.name}
        </h1>
        <p className="mt-2 text-muted-foreground text-sm max-w-xl font-medium">
          {t("account.dashboard.subtitle")}
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid gap-4 sm:grid-cols-3">
        {[
          {
            label: t("account.dashboard.activeQuotes"),
            value: activeQuotesCount,
            icon: ClipboardList,
            color: "text-primary bg-primary/10 border-primary/20",
          },
          {
            label: t("account.dashboard.completedOrders"),
            value: completedOrdersCount,
            icon: Package,
            color: "text-mint-ink bg-mint/10 border-mint/20",
          },
          {
            label: t("account.dashboard.pendingDocuments"),
            value: pendingDocsCount,
            icon: FileText,
            color: "text-teal bg-teal/10 border-teal/20",
          },
        ].map((s) => (
          <div
            key={s.label}
            className="glass-card rounded-2xl p-6 border border-border/60 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-between group"
          >
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                {s.label}
              </div>
              <div className="mt-2 font-display text-3xl font-black text-navy">{s.value}</div>
            </div>
            <div
              className={cn(
                "p-3.5 rounded-xl border transition-all duration-300 group-hover:scale-110",
                s.color,
              )}
            >
              <s.icon className="h-6 w-6" />
            </div>
          </div>
        ))}
      </div>

      {/* Recent Quotes Section */}
      <div className="glass-card rounded-2xl border border-border/60 overflow-hidden shadow-sm flex flex-col">
        <div className="border-b border-border/60 px-6 py-4.5 flex items-center justify-between bg-surface-alt/40">
          <h2 className="font-display text-lg font-bold text-navy flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-primary" />
            {t("account.dashboard.recentQuotes") || "Submitted Quotes & RFQs"}
          </h2>
          <Link
            to="/account/quotes"
            className="text-xs font-bold text-primary hover:underline flex items-center gap-1"
          >
            {t("account.dashboard.viewAllQuotes") || "View all quotes"}
            <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="divide-y divide-border/40">
          {quotes.length === 0 ? (
            <div className="flex h-[180px] flex-col items-center justify-center text-center p-6 space-y-2">
              <ClipboardList className="h-8 w-8 text-muted-foreground/60" />
              <p className="text-xs font-bold text-navy/60">
                {t("account.dashboard.noQuotes") || "No quote requests submitted yet"}
              </p>
              <Link to="/request-quote">
                <Button variant="outline" size="sm" className="rounded-xl text-xs font-bold mt-2">
                  {t("account.quotes.requestNow") || "Request a Quote"}
                </Button>
              </Link>
            </div>
          ) : (
            quotes.slice(0, 3).map((q) => {
              const waUrl = formatWhatsAppFollowUpUrl(q);
              const mailUrl = formatEmailFollowUpUrl(q);
              return (
                <div
                  key={q.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-4 sm:px-6 sm:py-4 gap-3 hover:bg-muted/30 transition-colors"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-bold px-2 py-0.5 rounded bg-background border border-border/80 text-navy">
                        {q.id}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(q.date).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </span>
                    </div>
                    <div className="font-bold text-navy text-sm">
                      {q.productTitle || "General Supply Inquiry"}
                    </div>
                  </div>

                  <div className="flex items-center gap-2.5">
                    <span
                      className={cn(
                        "inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold border",
                        q.status === "Quoted"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200/50"
                          : q.status === "Under Review"
                            ? "bg-sky-50 text-sky-700 border-sky-200/50"
                            : "bg-amber-50 text-amber-700 border-amber-200/50",
                      )}
                    >
                      {q.status}
                    </span>

                    <a
                      href={waUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700 shadow-xs"
                      title="Follow up on WhatsApp"
                    >
                      <MessageCircle className="h-3 w-3" />
                      <span>WhatsApp</span>
                    </a>
                    <a
                      href={mailUrl}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold bg-surface-alt text-navy hover:bg-muted border border-border/80"
                      title="Follow up via Email"
                    >
                      <Mail className="h-3 w-3 text-primary" />
                      <span>Email</span>
                    </a>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Recent Activity Table (Orders) */}
        <div className="glass-card rounded-2xl border border-border/60 overflow-hidden shadow-sm flex flex-col">
          <div className="border-b border-border/60 px-6 py-4.5 flex items-center justify-between bg-surface-alt/40">
            <h2 className="font-display text-lg font-bold text-navy flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              {t("account.dashboard.recentOrders") || "Recent Orders"}
            </h2>
            <Link to="/account/orders" className="text-xs font-bold text-primary hover:underline">
              {t("account.dashboard.viewAll")}
            </Link>
          </div>
          <div className="flex-1 divide-y divide-border/40">
            {orders.length === 0 ? (
              <div className="flex h-[200px] flex-col items-center justify-center text-center p-6 space-y-2">
                <Package className="h-8 w-8 text-muted-foreground/60" />
                <p className="text-xs font-bold text-navy/60">{t("account.dashboard.noOrders")}</p>
              </div>
            ) : (
              orders.slice(0, 4).map((o) => {
                let StatusIcon = Clock;
                let statusColor = "text-amber-600 bg-amber-50 border-amber-200/50";
                if (o.status === "Approved") {
                  StatusIcon = ShieldCheck;
                  statusColor = "text-emerald-600 bg-emerald-50 border-emerald-200/50";
                } else if (o.status === "Cancelled") {
                  StatusIcon = AlertCircle;
                  statusColor = "text-rose-600 bg-rose-50 border-rose-200/50";
                } else if (o.status === "Shipped") {
                  StatusIcon = Package;
                  statusColor = "text-sky-600 bg-sky-50 border-sky-200/50";
                }

                return (
                  <div
                    key={o.id}
                    className="flex items-center justify-between px-6 py-4.5 hover:bg-muted/30 transition-colors group"
                  >
                    <div className="space-y-1">
                      <div className="font-bold text-navy text-sm flex items-center gap-1.5">
                        {o.id}
                        <ChevronRight className="h-3 w-3 text-muted-foreground/50 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <div className="text-xs text-muted-foreground font-medium truncate max-w-[200px] sm:max-w-[260px]">
                        {o.products.join(", ")}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold text-navy">{o.total ?? "—"}</span>
                      <span
                        className={cn(
                          "inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border",
                          statusColor,
                        )}
                      >
                        <StatusIcon className="h-3 w-3 shrink-0" />
                        {o.status}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Essential Documents Checklist */}
        <div className="glass-card rounded-2xl border border-border/60 overflow-hidden shadow-sm flex flex-col">
          <div className="border-b border-border/60 px-6 py-4.5 flex items-center justify-between bg-surface-alt/40">
            <h2 className="font-display text-lg font-bold text-navy flex items-center gap-2">
              <FileText className="h-4 w-4 text-teal" />
              {t("account.dashboard.customsDocuments")}
            </h2>
            <Link to="/account/customs" className="text-xs font-bold text-teal hover:underline">
              {t("account.dashboard.manageDocs")}
            </Link>
          </div>
          <div className="flex-1 divide-y divide-border/40">
            {documents.length === 0 ? (
              <div className="flex h-[200px] flex-col items-center justify-center text-center p-6 space-y-2">
                <FileText className="h-8 w-8 text-muted-foreground/60" />
                <p className="text-xs font-bold text-navy/60">
                  {t("account.dashboard.noDocuments")}
                </p>
              </div>
            ) : (
              documents.slice(0, 4).map((d) => (
                <div
                  key={d.id}
                  className="flex items-center justify-between px-6 py-4.5 hover:bg-muted/30 transition-colors"
                >
                  <div className="space-y-1">
                    <div className="font-bold text-navy text-sm truncate max-w-[240px]">
                      {d.name}
                    </div>
                    <div className="text-xs text-muted-foreground font-medium">
                      {d.type} · {d.date}
                    </div>
                  </div>
                  <span className="text-xs font-bold text-teal bg-teal/10 px-2 py-0.5 rounded border border-teal/20 uppercase tracking-wide">
                    {t("account.dashboard.available")}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
